/**
 * All analytics items get:
 * {
 *  event: "Analytics",
 *  action: "click",
 *  label: "text",
 *  category: scope (see CATS)
 * }
 * NOTE: Any LABEL set down below must be an array of objects.
 * IF THE ARRAY IS NESTED, it must be ANOTHER array of objects,
 * BECAUSE our Nav() util is recursive when it finds an object instead
 * of a string.
 */

const commonProps = {
  event: "analytics",
  action: "click"
}

// categories
const CATS = {
  // nav
  HEADER: "header",
  MENU: "main-menu",
  FOOTER: "footer",
  // forms
  FORMS: "form field",
  // ctas
  HERO: "cta button - hero",
  SPOTLIGHT: "cta button - spotlight",
  BANNER: "cta button - banner",
  NEWS: "cta button - news",
  RESOURCES: "cta button - resources",
  NEWSROOM: "cta button - newsroom",
  MISC: "cta button",
  CTAS: "cta button", // for scope content ctas
  LISTS: "list",
  BADGES: "badge",
  IMGS: "image",
  CONTACT: "contact-us"
}

// navigation labels
const NAV_LABELS = [
  { EXO: "Exo Works" },
  { VISION: "Vision" },
  { TECH: "Technology" },
  { RESOURCES: "Resources" },
  { NEWSROOM: "Newsroom" },
  { PEOPLE: "Our People" },
  { CAREERS: "Careers" },
  { SUPPORT: "Support" },
  { CONTACT: "Contact" },
  { INVESTORS: "Investors" }
]

// form labels
const FORM_LABELS = [
  { YEMAIL: "your email" },
  { EMAIL: "email address" },
  { FNAME: "first name" },
  { LNAME: "last name" },
  { PHONE: "phone number" },
  { ROLE: "your role" },
  { DEPARTMENT: "your department" },
  { COMPANY: "company name" },
  { ZIPCODE: "zip code" },
  { TOPIC: "topic" },
  { DESC: "description" },
  { SUBMIT: "form submit" }
]

// CTA labels
const CTA_LABELS_HERO = [{ EXPLORE: "explore" }]
// most of these are driven by content
// i.e. the tagging gets a category of "list"
// and then the tagging is the commonProps + label = content
const CTA_LABELS_SPOTLIGHT = []
const CTA_LABELS_BANNER = []
const CTA_LABELS_NEWS = [{ READMORE: "read more" }]
const CTA_LABELS_RESOURCES = [{ READMORE: "read more - resources" }]
const CTA_LABELS_NEWSROOM = [{ READMORE: "read more - newsroom" }]
const CTA_LABELS_MISC = [
  { TRYEXO: "try it out" },
  { TECHSPECS: "view all tech specs" },
  { LEARN: "learn more" },
  { JOINUS: "find your role" },
  { PEOPLE: "person link" },
  { OURPEOPLE: "see more - our people" },
  { SUPPORT: "support question" }
]

// most of these are driven by content
// i.e. the tagging gets a category of "list"
// and then the tagging is the commonProps + label = content
const LIST_LABELS = []

const BADGE_LABELS = [
  { PODSPOTIFY: "spotify podcast" },
  { PODGOOGLE: "google podcast" },
  { PODAPPLE: "apple podcast" }
]

// just like lists, these are largely content driven
const IMG_LABELS = []
// contact links are content driven
const CONTACT_LABELS = []

/* *************************************************************************************************** */
/* ************************ utilities and assembling things below this point ************************* */
/* *************************************************************************************************** */

const getGeneric = scope => ({
  ...commonProps,
  category: scope
})

// this is exported as GENERIC.CONTENT() for use by analytics.utils
const getGenericTag = (scope, content) => ({
  ...commonProps,
  category: CATS[scope],
  label: content
})

/**
 * NAV('scope') to get analytics objects
 * @param  {string} scope - the category
 * @return {object}
 * this structure just sets up
 * key: { label, commonProps, category }
 * in the end we get
 * HEADER.NAV.EXO, HEADER.NAV.TECH, etc
 * KEEP IN MIND the recursion if the third level deep is
 */
const NAV = (scope, labels) => {
  const common = getGeneric(scope)

  const expanded = {}
  labels.forEach(v => {
    Object.entries(v).forEach(([key, value]) => {
      if (typeof value !== "string" && typeof value === "object") {
        expanded[key] = NAV(key, v[key])
      } else {
        expanded[key] = {
          ...common,
          label: value
        }
      }
    })
  })
  return {
    ...expanded
  }
}

const MENUS = {
  HEADER: {
    NAV: NAV(CATS.HEADER, NAV_LABELS)
  },
  MENU: {
    NAV: NAV(CATS.MENU, NAV_LABELS)
  },
  FOOTER: {
    NAV: NAV(CATS.FOOTER, NAV_LABELS)
  }
}

const FORMS = {
  FIELDS: NAV(CATS.FORMS, FORM_LABELS)
}

const CTAS = {
  HERO: NAV(CATS.HERO, CTA_LABELS_HERO),
  SPOTLIGHT: NAV(CATS.SPOTLIGHT, CTA_LABELS_SPOTLIGHT),
  BANNER: NAV(CATS.BANNER, CTA_LABELS_BANNER),
  NEWS: NAV(CATS.NEWS, CTA_LABELS_NEWS),
  RESOURCES: NAV(CATS.RESOURCES, CTA_LABELS_RESOURCES),
  NEWSROOM: NAV(CATS.NEWSROOM, CTA_LABELS_NEWSROOM),
  MISC: NAV(CATS.MISC, CTA_LABELS_MISC)
}

const LISTS = {
  LISTS: NAV(CATS.LISTS, LIST_LABELS)
}

const BADGES = {
  BADGES: NAV(CATS.BADGES, BADGE_LABELS)
}

const IMGS = {
  IMGS: NAV(CATS.IMGS, IMG_LABELS)
}

const CONTACT = {
  CONTACT: NAV(CATS.CONTACT, CONTACT_LABELS)
}

const ANALYTICS = {
  ...MENUS,
  FORMS,
  CTAS,
  ...LISTS,
  ...BADGES,
  ...IMGS,
  ...CONTACT
}

const GENERIC = {
  CONTENT: getGenericTag
}

// console.log("ANALYTICS constant:", ANALYTICS, GENERIC)

export { GENERIC }

export default ANALYTICS
